import { useRef, useEffect } from 'react';

/**
 * Global singleton Audio instance - shared across all hook instances
 * This ensures only ONE audio element exists and plays at a time
 */
let globalAudioInstance = null;
let globalIsPlaying = false;
let globalIsCallAccepted = false; // CRITICAL: Prevents ringtone after accept
let vibrationInterval = null; // Track vibration interval for cleanup
let userInteractionEnabled = false; // Track if user has interacted (enables audio)
let globalAudioContext = null; // WebAudio unlock helper (best-effort)

/**
 * Custom hook for managing incoming call ringtone
 * 
 * Features:
 * - Uses SINGLE global Audio instance (not recreated)
 * - Prevents playing after call is accepted
 * - Preloads ringtone for faster playback
 * - Handles autoplay restrictions
 * - Loops ringtone until stopped
 * - Cleanly stops and resets audio
 * 
 * @returns {Object} Object with play and stop methods
 */
const useRingtone = () => {
  const audioRef = useRef(null);
  const isPlayingRef = useRef(false);
  const playAttemptRef = useRef(false);

  // Enable audio on user interaction (required for mobile browsers)
  useEffect(() => {
    const enableAudioOnInteraction = () => {
      if (!userInteractionEnabled) {
        userInteractionEnabled = true;
        console.log('✅ User interaction detected - audio enabled');

        // Best-effort: unlock WebAudio context (helps on some devices)
        try {
          if (!globalAudioContext) {
            const Ctx = window.AudioContext || window.webkitAudioContext;
            if (Ctx) globalAudioContext = new Ctx();
          }
          if (globalAudioContext && globalAudioContext.state === 'suspended') {
            globalAudioContext.resume().catch(() => {});
          }
        } catch (e) {}
        
        // Try to unlock audio context if available
        if (globalAudioInstance) {
          try {
            // Some browsers require a successful play in a user gesture to unlock future plays.
            // We try a very short muted play then stop immediately.
            const prevMuted = globalAudioInstance.muted;
            globalAudioInstance.muted = true;
            const p = globalAudioInstance.play();
            if (p && typeof p.then === 'function') {
              p.then(() => {
                globalAudioInstance.pause();
                globalAudioInstance.currentTime = 0;
                globalAudioInstance.muted = prevMuted;
              }).catch(() => {
                globalAudioInstance.muted = prevMuted;
              });
            } else {
              globalAudioInstance.pause();
              globalAudioInstance.currentTime = 0;
              globalAudioInstance.muted = prevMuted;
            }
          } catch (e) {
            // ignore
          }
        }
      }
    };

    // Listen for any user interaction
    document.addEventListener('click', enableAudioOnInteraction, { once: true });
    document.addEventListener('touchstart', enableAudioOnInteraction, { once: true });
    document.addEventListener('keydown', enableAudioOnInteraction, { once: true });

    return () => {
      document.removeEventListener('click', enableAudioOnInteraction);
      document.removeEventListener('touchstart', enableAudioOnInteraction);
      document.removeEventListener('keydown', enableAudioOnInteraction);
    };
  }, []);

  // Initialize SINGLE global audio element (only once)
  useEffect(() => {
    // Use global singleton - create only if it doesn't exist
    if (!globalAudioInstance) {
      const ringtonePath = '/sounds/incoming_call.mp3';
      const fallbackPath = '/sounds/mood_ringtone.mp3';
      
      // Create SINGLE global audio instance
      globalAudioInstance = new Audio(ringtonePath);
      let errorLogged = false;
      let fallbackTried = false;
      
      // Configure audio settings for better mobile compatibility
      globalAudioInstance.loop = true;
      globalAudioInstance.volume = 0.8;
      globalAudioInstance.preload = 'auto';
      
      // Mobile browser optimizations
      globalAudioInstance.setAttribute('playsinline', 'true'); // iOS
      globalAudioInstance.setAttribute('webkit-playsinline', 'true'); // iOS Safari
      
      // Handle audio errors - try fallback
      const handleError = (e) => {
        if (errorLogged) return;
        errorLogged = true;
        
        if (!fallbackTried) {
          fallbackTried = true;
          console.warn('⚠️ Primary ringtone not found. Trying fallback...');
          
          const fallbackAudio = new Audio(fallbackPath);
          fallbackAudio.loop = true;
          fallbackAudio.volume = 0.8;
          fallbackAudio.preload = 'auto';
          
          fallbackAudio.addEventListener('error', () => {
            console.warn('⚠️ Ringtone files not found. Please add:');
            console.warn('   📁 frontend/public/sounds/incoming_call.mp3');
          });
          
          fallbackAudio.addEventListener('canplaythrough', () => {
            console.log('✅ Using fallback ringtone');
            globalAudioInstance = fallbackAudio;
            audioRef.current = fallbackAudio;
          });
          
          fallbackAudio.load();
          return;
        }
      };

      let loadedLogged = false;
      globalAudioInstance.addEventListener('error', handleError);
      globalAudioInstance.addEventListener('canplaythrough', () => {
        if (!errorLogged && !loadedLogged) {
          console.log('✅ Global ringtone loaded and ready');
          loadedLogged = true;
        }
      });
    }
    
    // Reference the global instance
    audioRef.current = globalAudioInstance;

    // Cleanup on unmount (but don't destroy global instance)
    return () => {
      // Just clear local reference, keep global instance alive
      audioRef.current = null;
    };
  }, []);

  /**
   * Play the ringtone
   * CRITICAL: Checks globalIsCallAccepted flag - won't play if call was accepted
   * Enhanced for mobile browsers with retry logic and vibration fallback
   */
  const playRingtone = async () => {
    // CRITICAL CHECK: Don't play if call was already accepted
    if (globalIsCallAccepted) {
      console.log('🚫 Ringtone blocked - call already accepted');
      return;
    }

    const audio = audioRef.current || globalAudioInstance;
    if (!audio) {
      console.warn('⚠️ Ringtone audio not initialized');
      return;
    }

    // Don't play if already playing globally
    if (globalIsPlaying || !audio.paused) {
      console.log('🔔 Ringtone already playing');
      return;
    }

    // Don't play if play attempt in progress
    if (playAttemptRef.current) {
      console.log('🔔 Play attempt already in progress');
      return;
    }

    playAttemptRef.current = true;

    // Try to vibrate on mobile devices (fallback if audio fails)
    // Continuous vibration pattern until call is answered
    const startVibration = () => {
      if ('vibrate' in navigator && !vibrationInterval) {
        try {
          // Vibrate pattern: vibrate for 200ms, pause 100ms
          const vibratePattern = [200, 100];
          
          // Start continuous vibration
          const vibrateLoop = () => {
            if (!globalIsCallAccepted && !globalIsPlaying) {
              navigator.vibrate(vibratePattern);
              vibrationInterval = setTimeout(vibrateLoop, 300); // Repeat every 300ms
            }
          };
          
          vibrateLoop();
          console.log('📳 Vibration started as fallback');
        } catch (e) {
          console.warn('⚠️ Vibration not supported or failed');
        }
      }
    };

    const stopVibration = () => {
      if (vibrationInterval) {
        clearTimeout(vibrationInterval);
        vibrationInterval = null;
      }
      if ('vibrate' in navigator) {
        try {
          navigator.vibrate(0); // Stop vibration
        } catch (e) {}
      }
    };

    // Try to play with multiple attempts (for mobile browsers)
    const attemptPlay = async (retryCount = 0) => {
      try {
        // Reset audio to beginning
        audio.currentTime = 0;
        
        // Set volume explicitly (some mobile browsers need this)
        audio.volume = 0.8;
        
        // Attempt to play
        const playPromise = audio.play();
        
        if (playPromise !== undefined) {
          await playPromise;
          globalIsPlaying = true;
          isPlayingRef.current = true;
          playAttemptRef.current = false;
          console.log('🔔 Ringtone started successfully');
          
          // Stop vibration if audio is playing
          stopVibration();
        }
      } catch (error) {
        // Handle autoplay restrictions
        if (error.name === 'NotAllowedError' || error.name === 'NotSupportedError') {
          console.warn('⚠️ Autoplay blocked by browser (attempt ' + (retryCount + 1) + ')');
          
          // Try vibration as fallback
          if (retryCount === 0) {
            startVibration();
          }

          // If user hasn't interacted yet, retry on the NEXT user gesture (much more reliable than timeouts)
          if (!userInteractionEnabled) {
            const retryOnGesture = () => {
              document.removeEventListener('click', retryOnGesture);
              document.removeEventListener('touchstart', retryOnGesture);
              document.removeEventListener('keydown', retryOnGesture);
              attemptPlay(retryCount + 1);
            };
            document.addEventListener('click', retryOnGesture, { once: true });
            document.addEventListener('touchstart', retryOnGesture, { once: true });
            document.addEventListener('keydown', retryOnGesture, { once: true });

            playAttemptRef.current = false;
            return;
          }
          
          // Retry after a short delay (mobile browsers sometimes need time)
          if (retryCount < 3) { // Increased retries for mobile
            setTimeout(() => {
              attemptPlay(retryCount + 1);
            }, 500); // Longer delay for mobile browsers
          } else {
            playAttemptRef.current = false;
            globalIsPlaying = false;
            isPlayingRef.current = false;
            console.warn('⚠️ Ringtone failed after retries - using vibration only');
            // Keep vibration going as fallback
            startVibration();
          }
        } else {
          console.error('❌ Error playing ringtone:', error);
          playAttemptRef.current = false;
          globalIsPlaying = false;
          isPlayingRef.current = false;
          // Try vibration as fallback
          startVibration();
        }
      }
    };

    // Start play attempt
    await attemptPlay(0);
  };

  /**
   * Stop the ringtone
   * CRITICAL: Sets globalIsCallAccepted flag to prevent future plays
   * Force stops even if playing state is incorrect
   * Also stops vibration if active
   */
  const stopRingtone = () => {
    // CRITICAL: Set flag to prevent any future play attempts
    const wasAccepted = globalIsCallAccepted;
    globalIsCallAccepted = true;
    
    const audio = audioRef.current || globalAudioInstance;
    
    try {
      // Stop audio if available..
      if (audio) {
        audio.pause();
        audio.currentTime = 0; // Reset to beginning
      }
      
      // Stop vibration if active
      if (vibrationInterval) {
        clearTimeout(vibrationInterval);
        vibrationInterval = null;
      }
      if ('vibrate' in navigator) {
        try {
          navigator.vibrate(0); // Stop vibration
        } catch (e) {
          // Vibration stop failed, ignore
        }
      }
      
      globalIsPlaying = false;
      isPlayingRef.current = false;
      playAttemptRef.current = false;
      
      // Only log once per call
      if (!wasAccepted) {
        console.log('🔕 Ringtone stopped - call accepted flag set');
      }
    } catch (error) {
      console.error('❌ Error stopping ringtone:', error);
      globalIsPlaying = false;
      isPlayingRef.current = false;
      playAttemptRef.current = false;
      
      // Ensure vibration stops even on error
      if (vibrationInterval) {
        clearTimeout(vibrationInterval);
        vibrationInterval = null;
      }
      if ('vibrate' in navigator) {
        try {
          navigator.vibrate(0);
        } catch (e) {}
      }
    }
  };

  /**
   * Reset the call accepted flag (for new calls)
   * Call this when a new incoming call arrives
   */
  const resetCallAccepted = () => {
    globalIsCallAccepted = false;
    console.log('🔄 Call accepted flag reset - ready for new call');
  };

  return {
    playRingtone,
    stopRingtone,
    resetCallAccepted, // Export reset function
    isPlaying: () => isPlayingRef.current || globalIsPlaying
  };
};

export default useRingtone;

